<template>
    <aside>
        <div class="nav-container-inside">
            <nav
                ref="nav"
                class="nav"
            >
                <router-link
                    class="nav__logo__link"
                    to="/"
                >
                    <div class="nav__logo">
                        <img
                            v-if="!drawer"
                            :src="`${uploadHost}/uploads/${uploadPath}/logo-small.png?rnd=${rnd}`"
                            class="nav__logo__img"
                            alt="Logo small"
                        >
                        <img
                            v-if="drawer"
                            :src="`${uploadHost}/uploads/${uploadPath}/logo.png?rnd=${rnd}`"
                            class="nav__logo__img"
                            alt="Logo"
                        >
                    </div>
                </router-link>

                <el-menu
                    :collapse="!drawer"
                    :default-openeds.sync="defaultOpeneds"
                    unique-opened
                    class="nav__list"
                    data-test="nav__list"
                >
                    <template v-for="module in availableModules">
                        <template v-if="subMenuItems[module.name] && subMenuItems[module.name].length">
                            <el-submenu
                                :key="module.name"
                                :index="module.name"
                                popper-class="nav__submenu-popper"
                            >
                                <template #title>
                                    <div class="nav__submenu-wrapper">
                                        <router-link
                                            :is="!module.notLinkable ? 'router-link' : 'p'"
                                            :to="{ name: module.name }"
                                            :class="[ 'nav__link', { 'router-link-exact-active': module.notLinkable && isNotLinkableModuleActive(module, $route.name) } ]"
                                            data-test="nav__link"
                                        >
                                            <icon
                                                :name="module.icon"
                                                class="nav__link__icon"
                                                size="small"
                                            />
                                            <span class="nav__link__text">{{ module.name }}</span>
                                        </router-link>
                                    </div>
                                </template>
                                <el-menu-item
                                    v-for="subMenuItem in subMenuItems[module.name]"
                                    :key="subMenuItem.name"
                                    :index="subMenuItem.to.name"
                                >
                                    <template #title>
                                        <router-link
                                            :to="{ name: subMenuItem.to.name }"
                                            class="nav__link"
                                            data-test="nav__sublink"
                                        >
                                            <span class="nav__link__text">{{ subMenuItem.name }}</span>
                                        </router-link>
                                    </template>
                                </el-menu-item>
                            </el-submenu>
                        </template>
                        <el-menu-item
                            v-else
                            :key="module.name"
                            :index="module.name"
                        >
                            <el-tooltip
                                :content="module.name"
                                :disabled="drawer"
                                placement="right"
                                :enterable="false"
                                :hide-after="3000"
                            >
                                <router-link
                                    :to="{ name: module.name }"
                                    class="nav__link"
                                    data-test="nav__link"
                                >
                                    <icon
                                        :name="module.icon"
                                        class="nav__link__icon"
                                        size="small"
                                    />
                                    <span class="nav__link__text">{{ module.name }}</span>
                                </router-link>
                            </el-tooltip>
                        </el-menu-item>
                    </template>
                </el-menu>

                <div class="collapse-wrapper">
                    <button
                        class="collapse-btn"
                        @click="$eventHub.$emit('toggleNav')"
                    >
                        <icon
                            :name="drawer ? '#double-chevron-left' : '#double-chevron-right'"
                            size="small"
                        />
                    </button>
                </div>
            </nav>
        </div>
    </aside>
</template>


<script>
import { mapGetters } from 'vuex';
import navigationModules from '@/store/dictionaries/navigationModules';
import defaultReports from  '@/store/dictionaries/defaultReports';
import eventHub from '@/eventHub';
import store from '@/store';

// Child components
import Icon     from 'shared-lib/components/ui/Icon';

export default {
    name: 'NavDrawer',

    components: { Icon },

    props: {
        drawer: {
            type: Boolean,
            default: true,
        },
    },

    data () {
        return {
            rnd: Date.now(),
            uploadHost: process.env.VUE_APP_UPLOADS_HOST,
            uploadPath: process.env.UPLOADS_PATH_NAME || 'upyield',
            defaultOpeneds: [],
        };
    },

    computed: {
        ...mapGetters([
            'currentUserHasModulePrivileges',
            'config',
            'currentUserHasModuleAccess',
            'isFraudProtectionAvailable',
            'isTestToolsEnabled',
        ]),

        showScheduledActions () {
            return this.config?.feature?.scheduler?.enabled && this.currentUserHasModuleAccess('schedules', 'read');
        },

        showPostbackTestingAction () {
            return this.isTestToolsEnabled && this.currentUserHasModulePrivileges('tools');
        },

        availableModules () {
            return navigationModules.filter(item => {
                const hasModulePrivileges = !item.privilegeModule
                    || (this.currentUserHasModuleAccess(item.privilegeModule, 'read') || this.currentUserHasModuleAccess(item.privilegeModule, 'write'));
                const hasPermissions = !item.permissionsModuleName || this.currentUserHasModulePrivileges(item.permissionsModuleName);

                const marketplaceIntegration = !(['onboarding', 'approvals'].includes(item.permissionsModuleName) && !this.config.general.marketplace_integration);

                const customGuard = this.$router.options.routesFlatten?.find?.(route => route.name === item.name)?.meta?.customGuard;
                const customGuardPassed = !customGuard || (customGuard && customGuard?.(store));

                const customRestrictions = {
                    'Control & Settings': this.controlAndSettingsAvailable,
                }[item.name] ?? true;

                return hasModulePrivileges && hasPermissions && marketplaceIntegration && customGuardPassed && customRestrictions;
            });
        },

        controlAndSettingsAvailable () {
            return this.showScheduledActions
                || ['read', 'write'].some(privilege => this.currentUserHasModuleAccess('traffic_filtering', privilege))
                || this.isFraudProtectionAvailable;
        },

        subMenuItems () {
            return {
                Reports: defaultReports(),
                Offers: [
                    this.currentUserHasModuleAccess('applications', 'list') && {
                        name: 'Applications',
                        to: { name: 'Applications' },
                    },
                ].filter(Boolean),
                Publishers: [
                    this.showPostbackTestingAction && {
                        name: 'Postback Testing',
                        to: { name: 'PostbackTesting' },
                    },
                ].filter(Boolean),
                'Control & Settings': [
                    this.showScheduledActions && {
                        name: 'Scheduled Actions',
                        to: { name: 'Scheduled actions' },
                    },
                    ['read', 'write'].some(privilege => this.currentUserHasModuleAccess('traffic_filtering', privilege)) && {
                        name: 'Traffic Filtering',
                        to: { name: 'Traffic Filtering' },
                    },
                    // TODO: add blacklist_control module to control privs?
                    // TODO: unhide once it's QAed
                    ['read', 'write'].some(privilege => this.currentUserHasModuleAccess('traffic_filtering', privilege)) && {
                        name: 'Blacklist Control',
                        to: { name: 'BlacklistControl' },
                    },
                    this.isFraudProtectionAvailable && {
                        name: 'Fraud Protection',
                        to: { name: 'FraudProtection' },
                    },
                    ['read', 'write'].some(privilege => this.currentUserHasModuleAccess('traffic_control', privilege)) && {
                        name: 'Traffic Control',
                        to: { name: 'TrafficControl' },
                    },
                    ['read', 'write'].some(privilege => this.currentUserHasModuleAccess('kpi_control', privilege)) && {
                        name: 'KPI Control',
                        to: { name: 'KPIControl' },
                    },
                    ['read', 'write'].some(privilege => this.currentUserHasModuleAccess('foreign_leads', privilege)) && {
                        name: 'External Campaigns',
                        to: { name: 'ForeignLeadsCampaigns' },
                    },
                ].filter(Boolean),
            };
        },
    },

    watch: {
        '$route.name': 'openMatchedSubmenu',
    },

    mounted () {
        this.$emit('onNavMounted', this.$refs.nav);

        this.openMatchedSubmenu();

        eventHub.$on('customization-update', () => {
            this.rnd = Date.now();
        });
    },

    methods: {
        openMatchedSubmenu () {
            Object.entries(this.subMenuItems).find((entry) => {
                const [name, values] = entry;

                values.forEach((value) => {
                    if (value.to.name === this.$route.name) {
                        this.defaultOpeneds = [ name ];
                    }
                });
            });
        },

        isNotLinkableModuleActive (module, route) {
            return this.subMenuItems[module.name].some((subItem) => subItem.to.name === route);
        },
    },
};
</script>

<style lang="scss" scoped>
    .nav-container {
        position: fixed;
        float: left;
        transition: 0.2s ease-in-out;
        z-index: 10;
        width: $navigation-width;
        height: 100%;
        background-color: $color-brand-background;
        border-right: 1px solid $border-color--nav;
        overflow: hidden;

        .theme-dark & {
            background-color: $color-brand-background-dark;
        }

        .collapse-wrapper {
            position: fixed;
            bottom: 0;
            width: $navigation-width - 1px;
            background-color: $color-brand-background;
            border-top: 1px solid $border-color--nav;

            .theme-dark & {
                background-color: $color-brand-background-dark;
                border-top-color: lighten($color-brand-background-dark, 20%);
            }

            .collapse-btn {
                width: 100%;
                text-align: right;
                color: #ccc;
                float: right;
                padding-right: 10px;

                &:hover {
                    background-color: $border-color--nav;

                    .theme-dark & {
                        background-color: lighten($color-brand-background-dark, 10%);
                    }
                }
            }
        }

        &.nav-container--closed {
            width: $navigation-width--small;
            overflow-x: hidden;

            @include media-breakpoint-down(sm) {
                width: $navigation-width--mobile;
            }

            .nav {
                .nav__logo{
                    width: 40px;
                    margin-left: 20px;
                    transition: none;

                    @include media-breakpoint-down(sm) {
                        width: 30px;
                        height: 30px;
                        margin-left: 10px;
                    }
                }

                .collapse-wrapper {
                    width: $navigation-width--small - 1px;

                    @include media-breakpoint-down(sm) {
                        width: $navigation-width--mobile - 1px;
                    }

                    .collapse-btn {
                        text-align: center;
                        padding: 0;
                    }
                }

                .nav__link {
                    margin-left: 10px;
                }

                .nav__link__text {
                    transition: padding 1s ease;
                    padding: 0;
                }

                .nav__link__icon {
                    margin: 0;
                }
            }
        }
    }

    .nav {
        $logo-height: 130px;

        position: relative;
        overflow: hidden;

        .nav__logo {
            overflow: hidden;
            width: 160px;
            height: 40px;
            margin: 30px 10px 60px 20px;
            transition: 0.2s ease-in-out;
            position: fixed;
            top: 0;

            @include media-breakpoint-down(sm) {
                margin-bottom: 20px;
            }

            .nav__logo__img {
                width: 100%;
                height: 100%;
                pointer-events: none;
            }

            .nav__logo__link {
                position: fixed;
                top:0;
                left:0;
                overflow: hidden;
                display: block;
                width: $navigation-width - 1px;
                height: $logo-height;
                background: linear-gradient(to bottom, rgba(247, 248, 249,1) 71%,rgba(247, 248, 249,0) 100%);
            }
        }
    }

    .el-menu-item,
    .el-submenu {
        user-select: none;

        .nav__link {
            font-weight: 500;
            font-size: 14px;
            white-space: nowrap;
            outline: none;
            color: $font-color--default;
            display: block;

            .theme-dark & {
                color: $font-color--light;
            }

            .nav__link__text {
                transition: padding 1s ease;
                padding-left: 10px;
            }
        }

        .router-link-exact-active,
        .router-link-active[data-test='nav__link'],
        &:hover {
            color: $font-color--active;

            > .nav__link,
            > .el-submenu__title .nav__link,
            .nav__link__icon {
                color: $font-color--active;
            }
        }

        .router-link-exact-active .nav__link__text {
            color: $font-color--active;
        }

        .nav__link__icon {
            color: $font-color--light;
        }
    }

    .el-submenu {
        &.is-opened {
            background-color: $color-gray--lighten-5;

            .theme-dark & {
                background-color: lighten($color-brand-background-dark, 10%);
            }

            .router-link-active .nav__link__text {
                color: $font-color--active;
            }
        }

        .el-menu {
            .el-menu-item {
                line-height: 28px;
                height: 35px;
            }
            .nav__link {
                padding-left: 43px;
                font-weight: $font-weight--normal;

                .nav__link__text {
                    transition: padding 1s ease;
                    padding-left: 0;
                }
            }
        }

        .el-menu-item:last-child {
            margin-bottom: .7em;

            .el-menu--collapse & {
                margin-bottom: 0;
            }
        }
        /deep/ .el-submenu__icon-arrow {
            padding: 5px 0;
        }
    }

    .el-menu-item,
    /deep/ .el-submenu__title {
        &:hover, &:active, &:focus {
            background-color: transparent;
        }
    }

    .nav__list {
        width: 100%;
        height: calc(100vh - 140px - 35px);
        margin-bottom: 40px;
        margin-top: 140px;
        border-right: 0;
        background-color: transparent;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .el-menu .el-menu-item,
    .el-menu /deep/ .el-submenu__title {
        @include media-breakpoint-down(sm) {
            padding: 0 5px !important; // due to inline style
        }
    }

    .el-menu:not(.el-menu--collapse) .el-menu-item,
    .el-menu:not(.el-menu--collapse) /deep/ .el-submenu__title {
        padding-left: 20px !important; // due to inline style
    }
</style>

<style lang="scss">
    .nav__submenu-popper {
        .el-menu {
            background-color: #fff;

            .theme-dark & {
                background-color: $color-brand-background-dark;
            }
        }
        .el-menu-item,
        .el-submenu {
            line-height: 36px;
            height: 38px;
            user-select: none;

            &:hover{
                color: $font-color--active;

                .el-submenu__title,
                .nav__link__icon {
                    color: $font-color--active;
                }
            }

            .nav__link {
                padding-left: 0;
                font-size: 14px;
                white-space: nowrap;
                outline: none;
            }
        }

        .el-menu-item,
        /deep/ .el-submenu__title {
            &:hover, &:active, &:focus {
                background-color: rgba($color-gray--lighten-5, .5);

                .theme-dark & {
                    background-color: lighten($color-brand-background-dark, 10%);
                }
            }
        }
    }

    .el-menu-item,
    .el-submenu__title,
    .nav__link {
        height: 46px !important;
    }

    .nav__link,
    .nav__link__text {
        display: flex !important;
        align-items: center ;
    }

    .nav__link__text {
        height: 100% ;
    }

    .el-menu--inline {
        .el-menu-item,
        .nav__link {
            height: 36px !important;
        }
    }
</style>

